import React, { useEffect, useMemo, useState } from "react";

//common component
import Header from "../Components/Header";

//import data
import profileImage from "../Images/Avatar.png";
//styles
import "./Ranking.css";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import AnimatedCard, { MotionItem } from "../Components/AnimatedCard";

// Top 3 get a medal-style badge, everyone else a quiet number
const MEDALS = {
  1: "bg-amber-400/15 text-amber-300 ring-1 ring-amber-300/40",
  2: "bg-slate-300/15 text-slate-200 ring-1 ring-slate-200/40",
  3: "bg-orange-500/15 text-orange-300 ring-1 ring-orange-300/40",
};

function RankBadge({ rank }) {
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold transition-all duration-200 group-hover:scale-110 lg:text-[14px] ${MEDALS[rank] || "bg-white/5 text-[#8491A0] group-hover:bg-white/20 group-hover:text-white"
        }`}
    >
      {rank}
    </span>
  );
}

export default function Ranking({ setIsSidebarOpen, isSidebarOpen }) {
  const [rankings, setRankings] = useState(null);
  const [ownData, setOwnData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersQuery = query(
          collection(db, "users"),
          orderBy("totalScore", "desc"),
          limit(80)
        );

        const usersSnapshot = await getDocs(usersQuery);

        const userRankingsData = [];

        usersSnapshot.forEach((doc) => {
          const userData = {
            id: doc.id,
            self: Boolean(doc.id === auth?.currentUser?.uid),
            ...doc.data(),
            totalScore: doc.data().totalScore ? doc.data().totalScore : 0,
          };
          userRankingsData.push(userData);
        });

        const sortedRankings = userRankingsData.sort(
          (a, b) => b.totalScore - a.totalScore
        );
        const selfRanking = sortedRankings.findIndex((user) => user?.self) + 1;
        const selfData = sortedRankings.find((user) => user?.self);
        const selfRankingData = { ranking: selfRanking, ...selfData };

        setOwnData(selfRankingData);
        setRankings(sortedRankings);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Highest score is used to size each row's progress bar
  const maxScore = useMemo(
    () => Math.max(...(rankings || []).map((u) => Number(u.totalScore) || 0), 1),
    [rankings]
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="w-[100%]">
        <Header
          PageName={"Classement"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2">
        {/* My rank */}
        <AnimatedCard
          tilt={false}
          effects="light"
          className="relative shrink-0 overflow-hidden rounded-[15px] border border-white/30 bg-black/65 bg-primary p-[12px_14px] sm:p-[17px_22px]"
        >
          {/* soft glow, white only so it follows the theme */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/10 blur-3xl sm:-top-24 sm:-right-24 sm:h-64 sm:w-64" />

          <MotionItem className="relative flex items-center justify-between gap-2 sm:gap-3">
            <h2 className="min-w-0 text-left text-[14px] sm:text-[16px] lg:text-[20px] font-semibold text-white">
              Mon rang dans le classement
            </h2>
            {rankings && ownData?.ranking > 0 && (
              <span className="shrink-0 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] sm:px-3 sm:text-[11px] text-[#A0AEC0]">
                sur {rankings.length}
              </span>
            )}
          </MotionItem>

          <MotionItem className="relative mt-3 flex flex-col gap-3 rounded-2xl p-1 sm:mt-4 sm:gap-4 sm:p-4 transition-all duration-200 lg:flex-row lg:items-center lg:gap-8">
            {/* Rank + user */}
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <div
                className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl text-white sm:h-14 sm:w-14 sm:rounded-2xl lg:h-16 lg:w-16 ${MEDALS[ownData?.ranking] || "bg-white/10 ring-1 ring-white/20"
                  }`}
              >
                <span className="text-[9px] leading-none text-[#A0AEC0] sm:text-[10px]">
                  RANG
                </span>
                <span className="mt-1 text-[16px] font-bold leading-none sm:text-[20px] lg:text-[24px]">
                  {ownData?.ranking > 0 ? ownData.ranking : "—"}
                </span>
              </div>

              <img
                src={ownData?.photoURL || profileImage}
                alt={ownData?.pseudo || "Avatar"}
                className="h-10 w-10 shrink-0 rounded-lg object-cover  sm:h-12 sm:w-12 sm:rounded-xl lg:h-14 lg:w-14"
              />

              <div className="min-w-0 flex-1 text-start">
                <div className="truncate text-[14px] font-semibold text-white sm:text-[15px] lg:text-[18px]">
                  {ownData?.pseudo || ownData?.email?.split("@")[0] || "N/A"}
                </div>
                {ownData?.email && (
                  <div className="truncate text-[10px] text-[#A0AEC0] sm:text-[11px] lg:text-[13px]">
                    {ownData.email}
                  </div>
                )}
              </div>
            </div>

            {/* Function + Score: one compact row on mobile */}
            <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:gap-8">
              {ownData?.fonction && (
                <span className="w-fit max-w-full truncate rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] sm:text-[12px] text-[#A0AEC0]">
                  {ownData.fonction}
                </span>
              )}

              <div className="flex w-full flex-col gap-2 lg:w-56">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] sm:text-[11px] text-[#A0AEC0]">SCORE</span>
                  <span className="text-[20px] font-bold tabular-nums text-white sm:text-[22px] lg:text-[26px]">
                    {(Number(ownData?.totalScore) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10 sm:h-2">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-white/50 to-white"
                    style={{
                      width: `${Math.min(
                        ((Number(ownData?.totalScore) || 0) / maxScore) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </MotionItem>
        </AnimatedCard>

        {/* Full ranking */}
        <AnimatedCard
          tilt={false}
          effects="light"
          delay={0.15}
          className="all-ranking shrink-0 bg-primary pt-4 p-[12px_14px] sm:p-[17px_22px] rounded-[15px] border border-white/30 overflow-hidden flex flex-col justify-start "
        >
          <MotionItem className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-left text-[16px] md:text-[20px] font-semibold text-white">
                Classement
              </h2>
              <h3 className="hidden lg:block text-[#A0AEC0] text-[12px] md:text-[14px] text-left">
                Le Pouvoir de l’IA n’attend que toi..
              </h3>
            </div>
            {rankings && (
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-[#A0AEC0]">
                {rankings.length} participants
              </span>
            )}
          </MotionItem>

          <MotionItem className="w-full h-[520px] overflow-y-auto no-scrollbar text-white">
            <table className="w-full table-fixed border-separate border-spacing-y-2">
              <thead className="sticky top-0 z-10 bg-primary">
                <tr>
                  <th className="text-start py-3 ps-3 font-bold tracking-wider text-[10px] lg:text-[12px] text-white w-[62%] lg:w-[48%]">
                    PSEUDO
                  </th>
                  <th className="text-end lg:text-start py-3 pe-3 lg:pe-0 font-bold tracking-wider text-[10px] lg:text-[12px] text-white w-[38%] lg:w-[32%]">
                    SCORE
                  </th>
                  <th className="hidden lg:table-cell text-start py-3 font-bold tracking-wider text-[12px] text-white w-[20%]">
                    FONCTION
                  </th>
                </tr>
              </thead>

              <tbody>
                {rankings?.map((user, index) => {
                  const rank = index + 1;
                  const score = Number(user?.totalScore) || 0;
                  const percent = Math.max((score / maxScore) * 100, score > 0 ? 3 : 0);
                  const isSelf = user?.self;

                  const cell = `border-y transition-all duration-200 ${isSelf
                      ? "border-white/30 bg-white/[0.12] group-hover:bg-white/[0.22] group-hover:border-white/60"
                      : "border-transparent bg-white/[0.03] group-hover:border-white/40 group-hover:bg-white/[0.12]"
                    }`;

                  return (
                    <tr
                      key={user.id}
                      aria-current={isSelf ? "true" : undefined}
                      className="group cursor-default"
                    >
                      {/* Rank + user */}
                      <td
                        className={`${cell} border-s rounded-s-xl py-2.5 ps-3`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <RankBadge rank={rank} />
                          <img
                            src={user?.photoURL || profileImage}
                            alt={user?.pseudo || user?.displayName || "Avatar"}
                            className="h-10 w-10 shrink-0 rounded-lg object-cover  transition-all duration-200 "
                          />
                          <div className="min-w-0 text-start transition-transform duration-200 group-hover:translate-x-1">
                            <div className="truncate text-[12px] flex-col sm:flex-row flex lg:text-[15px] font-medium">
                              {user?.pseudo || user?.email?.split("@")[0] || "N/A"}
                              {isSelf && (
                                <span className="ms-2 rounded-full bg-white/15 sm:text-left text-center px-2 py-0.5 text-[10px] font-normal text-white">
                                  Moi
                                </span>
                              )}
                            </div>
                            <div className="truncate text-[10px] text-[#A0AEC0] lg:hidden">
                              {user?.fonction || "Employee"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Score + bar */}
                      <td
                        className={`${cell} py-2.5 pe-3 lg:pe-4 border-e lg:border-e-0 rounded-e-xl lg:rounded-e-none`}
                      >
                        <div className="flex flex-col items-end gap-1.5 lg:items-stretch">
                          <span className="text-[12px] md:text-[15px] font-semibold tabular-nums transition-transform duration-200 origin-left group-hover:scale-110">
                            {score.toFixed(2)}
                          </span>
                          <div className="h-1.5 w-full max-w-[160px] overflow-hidden rounded-full bg-white/10 transition-all duration-200 group-hover:h-2 group-hover:bg-white/20">
                            <div
                              className={`h-full rounded-full transition-colors duration-200 ${rank <= 3
                                  ? "bg-gradient-to-r from-white/60 to-white"
                                  : "bg-white/50 group-hover:bg-white"
                                }`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Function pill (desktop) */}
                      <td
                        className={`${cell} hidden lg:table-cell border-e rounded-e-xl py-2.5 pe-3`}
                      >
                        <span className="inline-block max-w-full truncate rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[12px] text-[#A0AEC0] transition-colors duration-200 group-hover:border-white/40 group-hover:bg-white/15 group-hover:text-white">
                          {user?.fonction || "Employee"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MotionItem>
        </AnimatedCard>
      </div>
    </div>
  );
}